import { cn } from '../../utils';
import { Avatar, AvatarFallback, AvatarImage } from '../core/avatar';

export interface ParticipantStackHost {
  name: string | null;
  avatarUrl: string | null;
}

export interface ParticipantStackProps {
  host: ParticipantStackHost;
  /** People invited to the event, excluding the host. */
  othersCount: number;
  size?: 'sm' | 'md';
  /** 'onRed' adapts borders/bubble for solid red surfaces (today agenda rows). */
  tone?: 'default' | 'onRed';
  className?: string;
  /** Accessible description, e.g. "Ana + 12 participants". */
  label?: string;
}

const sizeClasses: Record<NonNullable<ParticipantStackProps['size']>, string> = {
  sm: 'h-7 w-7 text-[10px]',
  md: 'h-9 w-9 text-xs',
};

function initialsFromName(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || '?';
}

function formatCount(count: number): string {
  if (count > 99) return '+99';
  return `+${count}`;
}

/**
 * Social-media style facepile for an event: the host's avatar overlapped by a
 * "+N" bubble for everyone else invited. Shared across p2e, teacher and
 * community — apps differ only in how they scope events, never in this UI.
 */
export function ParticipantStack({
  host,
  othersCount,
  size = 'md',
  tone = 'default',
  className,
  label,
}: ParticipantStackProps) {
  const circle = sizeClasses[size];
  const onRed = tone === 'onRed';
  return (
    <div
      className={cn('flex shrink-0 -space-x-2', className)}
      role="img"
      aria-label={label ?? [host.name, othersCount > 0 ? `+${othersCount}` : null].filter(Boolean).join(' ')}
    >
      <Avatar className={cn(circle, 'border-2', onRed ? 'border-white/60' : 'border-background')}>
        {host.avatarUrl ? <AvatarImage src={host.avatarUrl} alt={host.name ?? ''} /> : null}
        <AvatarFallback className="bg-[#fff1f5] font-semibold text-[#cf2e2e]">
          {initialsFromName(host.name)}
        </AvatarFallback>
      </Avatar>
      {othersCount > 0 && (
        <span
          className={cn(
            circle,
            'z-10 flex items-center justify-center rounded-full border-2 font-semibold tabular-nums',
            onRed
              ? 'border-white/60 bg-white/25 text-white'
              : 'border-background bg-rose-100 text-[#cf2e2e]',
          )}
        >
          {formatCount(othersCount)}
        </span>
      )}
    </div>
  );
}
